import { isHttpError } from '@jwplayer/companion';
import { JwApiError } from '@jwplayer/companion-gateway';
import { Store } from '@jwplayer/fe-common/module/store/react';
import { Suspense, useEffect, useMemo } from 'react';
import { useQuery, useQueryCache } from 'react-query';

import { PulseLoader } from '/component/loaders/pulse-loader';
import { PulseLoaderContainer } from '/component/loaders/pulse-loader/container';
import { PageError, type PageErrorType } from '/module/error/page-error';
import { useAbortController } from '/module/hooks';
import { usePermissions } from '/module/permissions';
import { useParams } from '/module/routing/hooks';
import type { InferRouteData, Route, RouteName } from '/module/routing/types';
import { updateUrlForCreatedResource } from '/module/routing/util';

import { ROUTES } from '../config';

type RouteResource<T> = {
  data: T;
  refetch: () => void;
  update: (updater: (currentData: T) => T) => void;
};

const routeStore = new Store<{
  [K in RouteName]: RouteResource<InferRouteData<K>>;
}>(
  (Object.keys(ROUTES) as RouteName[]).reduce(
    (acc, routeName) => {
      acc[routeName] = {
        data: undefined as never, // this will be populated before route loads
        refetch: () => {
          // noop
        },
        update: () => {
          // noop
        },
      };

      return acc;
    },
    {} as {
      [K in RouteName]: RouteResource<InferRouteData<K>>;
    },
  ),
);

export function useRouteResource<T extends RouteName>(routeName: T) {
  return routeStore.useState((s) => s[routeName]);
}

function getQueryKey<R extends RouteName>(routeName: R, siteId: string | undefined, resourceId: string | undefined) {
  return ['route-resource', routeName, siteId, resourceId];
}

function hasId(obj: unknown): obj is { id: string } {
  return Boolean(obj && typeof obj === 'object' && 'id' in obj && typeof obj.id === 'string');
}

const Loader = () => (
  <PulseLoaderContainer>
    <PulseLoader />
  </PulseLoaderContainer>
);

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Loader />}>{children}</Suspense>
);

function ResourceWrapper<R extends RouteName>({
  routeName,
  resourceId,
  routeResourceConfig,
  RouteComponent,
}: {
  routeName: R;
  resourceId: string | undefined | 'create';
  routeResourceConfig: NonNullable<Route['resource']>;
  RouteComponent: NonNullable<Route['component']>;
}) {
  type T = RouteResource<InferRouteData<R>>;

  const params = useParams();
  const queryCache = useQueryCache();
  const permissions = usePermissions();

  const abortController = useAbortController();

  const fetchResource = useMemo(() => {
    return async () => {
      return routeResourceConfig.fetch({ id: resourceId, params, permissions, abortController });
    };
  }, [abortController, resourceId, params, permissions, routeResourceConfig]);

  /**
   * Must run before the query below.
   * Otherwise, we might start a new fetch and immediately cancel it.
   */
  useEffect(() => {
    // When unmounting the route component
    return () => {
      // Abort any request that's still in progress
      abortController.abort();
    };
  }, [abortController]);

  const queryKey = useMemo(
    () => getQueryKey(routeName, params.siteId, resourceId),
    [routeName, resourceId, params.siteId],
  );
  const query = useQuery(queryKey, fetchResource, {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: routeResourceConfig.refetchInterval,
    retry: false,
    onSuccess: (data) => {
      routeStore.update((draft) => {
        draft[routeName].data = data as never;
      });
    },
  });

  const updateFn = useMemo(
    () => (updater: (currentData: T['data'] | undefined) => T['data'] | undefined) => {
      const currentData = queryCache.getQueryData<T['data']>(queryKey);

      const newData = updater(currentData);

      // When creating a resource, we go from `/create` to a `/{new_id}`
      if (queryKey.at(-1) === 'create' && hasId(newData)) {
        // Automatically update the url
        updateUrlForCreatedResource(newData.id);

        const newQueryKey = [...queryKey];
        newQueryKey.pop();
        newQueryKey.push(newData.id);
        // Set the query data for the new resource's id, instead of on `create`
        queryCache.setQueryData(newQueryKey, newData);
      } else {
        queryCache.setQueryData(queryKey, newData);
      }
    },
    [queryKey, queryCache],
  );

  useEffect(() => {
    routeStore.update((draft) => {
      draft[routeName].data = query.data as never;
      draft[routeName].refetch = query.refetch;
      draft[routeName].update = updateFn as never;
    });
  }, [routeName, query.data, query.refetch, updateFn]);

  useEffect(() => {
    return () => {
      // Remove query from cache only on resource unmount
      queryCache.removeQueries(queryKey);
    };
  }, [queryCache, queryKey]);

  if (query.isLoading) {
    return <Loader />;
  }

  if (query.isError) {
    let type: PageErrorType = 'error';

    let httpStatus: number | undefined;
    // companion v1
    if (isHttpError(query.error)) {
      httpStatus = query.error.httpStatus;
    }
    // companion v2
    else if (query.error instanceof JwApiError) {
      httpStatus = query.error.response?.status;
    }
    // fetch
    else if (query.error instanceof Response) {
      httpStatus = query.error.status;
    }

    switch (httpStatus) {
      case 400:
      case 404:
        type = 'notFound';
        break;
      case 401:
      case 403:
        type = 'permission';
        break;
    }

    throw new PageError(type, { debug: 'Route resource failed to load.' });
  }

  return (
    <SuspenseWrapper>
      <RouteComponent />
    </SuspenseWrapper>
  );
}

export function ResourcePreflight({
  RouteComponent,
  route,
  routeName,
}: {
  RouteComponent: NonNullable<Route['component']>;
  route: Route;
  routeName: RouteName;
}): React.ReactElement {
  const params = useParams();

  if (route.resource) {
    const id = route.resource.paramId ? params[route.resource.paramId] : undefined;

    return (
      <ResourceWrapper
        routeName={routeName}
        resourceId={id}
        routeResourceConfig={route.resource}
        RouteComponent={RouteComponent}
      />
    );
  }

  return (
    <SuspenseWrapper>
      <RouteComponent />
    </SuspenseWrapper>
  );
}
